import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export interface SectionVisibility {
  id: string;
  section_key: string;
  section_name_ar: string;
  section_name_en: string;
  is_visible: boolean;
  display_order: number;
}

export const useSectionVisibility = () => {
  const [sections, setSections] = useState<SectionVisibility[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { toast } = useToast();

  const fetchSections = async () => {
    try {
      const { data, error } = await supabase
        .from("section_visibility")
        .select("*")
        .order("display_order", { ascending: true });

      if (error) throw error;
      setSections(data || []);
    } catch (error) {
      console.error("Error fetching sections:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchSections();
  }, []);

  const toggleVisibility = async (sectionKey: string, isVisible: boolean) => {
    try {
      const { error } = await supabase
        .from("section_visibility")
        .update({ is_visible: isVisible })
        .eq("section_key", sectionKey);

      if (error) throw error;

      setSections((prev) =>
        prev.map((section) =>
          section.section_key === sectionKey
            ? { ...section, is_visible: isVisible }
            : section
        )
      );

      toast({
        title: isVisible ? "تم إظهار القسم" : "تم إخفاء القسم",
        description: isVisible ? "Section is now visible" : "Section is now hidden",
      });
    } catch (error) {
      console.error("Error updating section visibility:", error);
      toast({
        title: "خطأ",
        description: "Failed to update section visibility",
        variant: "destructive",
      });
    }
  };

  const updateOrder = async (reorderedSections: SectionVisibility[]) => {
    try {
      // Update local state immediately for smooth UX
      setSections(reorderedSections);

      // Update each section's display_order in database
      const updates = reorderedSections.map((section, index) =>
        supabase
          .from("section_visibility")
          .update({ display_order: index + 1 })
          .eq("id", section.id)
      );

      await Promise.all(updates);

      toast({
        title: "تم حفظ الترتيب",
        description: "Section order saved successfully",
      });
    } catch (error) {
      console.error("Error updating section order:", error);
      toast({
        title: "خطأ",
        description: "Failed to save section order",
        variant: "destructive",
      });
      // Refetch to restore correct order
      fetchSections();
    }
  };

  const isSectionVisible = (sectionKey: string): boolean => {
    // While loading, return false to prevent flash of content
    if (isLoading) return false;
    
    const section = sections.find((s) => s.section_key === sectionKey);
    return section?.is_visible ?? true;
  };

  return {
    sections,
    setSections,
    isLoading,
    toggleVisibility,
    updateOrder,
    isSectionVisible,
    refetch: fetchSections,
  };
};
